import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import type { BrandAttributionValue } from '../../config/instance-settings-registry';
import { BaseEntity } from './base.entity';
import { CompanyEntity } from './company.entity';
import { PortalSiteDomainEntity } from './portal-site-domain.entity';

/**
 * Mobile bottom-nav tab vocabulary for the storefront (PD-14609). The
 * portal admin picks up to four, in order; 'menu' is required (it is the
 * only path to the drawer, which carries sign-out, language, theme, and
 * the long-tail destinations).
 */
export const PORTAL_MOBILE_NAV_TABS = [
	'home',
	'shop',
	'account',
	'cart',
	'chat',
	'orders',
	'invoices',
	'menu',
] as const;
export type PortalMobileNavTab = (typeof PORTAL_MOBILE_NAV_TABS)[number];

/** Shipped default: Home / Shop / Account / Menu. */
export const DEFAULT_PORTAL_MOBILE_NAV_TABS: Array<PortalMobileNavTab> = [
	'home',
	'shop',
	'account',
	'menu',
];

/**
 * Persisted shape of `portal_site.settings`. All fields optional — missing
 * keys take the resolver's hardcoded defaults (see resolvePortalSiteSettings).
 *
 * Stock is split into two independent toggles per visitor type:
 *   - **availability**: the In Stock / Out of Stock pill (boolean shape).
 *     Cheap to expose and rarely sensitive — defaults to true for auth.
 *   - **quantity**: the actual number on hand. Often considered competitively
 *     sensitive — defaults to false even for auth so admins opt in.
 *
 * Quantity should only be considered visible if availability is also on;
 * the resolver treats quantity-without-availability as effectively false.
 *
 * Legacy keys (`anonymousShowStock`, `showStockToAuthenticated`) are still
 * read for back-compat with rows written before the split — see
 * `resolveEffectivePortalSiteSettings` for the migration logic.
 */
export interface PortalSiteSettings {
	/** Allow unauthenticated visitors to browse the catalog at all. Default false. */
	allowAnonymousBrowsing?: boolean;
	/** Show price to unauthenticated visitors. Only meaningful when anonymous browsing is allowed. Default false. */
	anonymousShowPricing?: boolean;
	/** Show "In Stock / Out of Stock" availability to anonymous visitors. Default false. */
	anonymousShowStockAvailability?: boolean;
	/** Show numeric quantity on hand to anonymous visitors. Default false; only meaningful when availability is also on. */
	anonymousShowStockQuantity?: boolean;
	/** Show "In Stock / Out of Stock" availability to authenticated users. Default true. */
	showStockAvailabilityToAuthenticated?: boolean;
	/** Show numeric quantity on hand to authenticated users. Default false; only meaningful when availability is also on. */
	showStockQuantityToAuthenticated?: boolean;

	// --- Authenticated-experience toggles (catalog-only-mode storefronts) ---
	/**
	 * Allow authenticated users to browse the product catalog at all —
	 * the Shop nav link, the header search, the catalog/category routes,
	 * and the catalog-based dashboard widgets. Default true.
	 *
	 * Off = an "account-portal-only" storefront: the customer can view
	 * order history, view/pay invoices, and reach an item's detail page
	 * ONLY by clicking a line item from their own order/invoice — there is
	 * no browse or search entry point. Because there's no way to put an
	 * arbitrary item in a cart in this mode, the resolver also forces
	 * `allowPurchasing` to false when this is off (see
	 * resolveEffectivePortalSiteSettings); the item detail reached from an
	 * order/invoice line therefore renders with no add-to-cart, matching
	 * the existing browse-only gating.
	 */
	allowCatalogBrowsing?: boolean;
	/**
	 * Allow authenticated users to add to cart and place orders. Some
	 * customers want a "browse-only" portal where logged-in users can see
	 * prices/stock but cannot purchase (e.g. quote-driven sales). Default
	 * true. Always coerced to false when `allowCatalogBrowsing` is off.
	 */
	allowPurchasing?: boolean;
	/**
	 * Hide item pricing across the entire storefront CATALOG — the catalog
	 * grid/list, product detail, favorites, dashboard item widgets, and all
	 * promo pricing (badges, strikethrough prices, headlines) — for every
	 * visitor, authenticated or anonymous (PD-14775). Order / invoice /
	 * statement / transaction history keeps its amounts: those are records
	 * of real transactions, unlike the per-user `hidePricing` flag which
	 * also suppresses the financial surfaces.
	 *
	 * Only meaningful on a browse-only storefront: purchasing without
	 * visible prices is incoherent, so the resolver coerces this to false
	 * whenever `allowPurchasing` is on (and the editor disables the
	 * checkbox). When active it also coerces `anonymousShowPricing` off so
	 * guests can't see prices the customers themselves can't. Default false.
	 */
	hideCatalogPricing?: boolean;
	/** Allow authenticated users to view their order history. Default true. */
	allowOrderHistory?: boolean;
	/**
	 * Show the "Ship Via" row on the storefront order-detail page. Default
	 * true. Off for tenants whose integration populates `shipVia` with an
	 * internal value that isn't meaningful to a customer — e.g. a food
	 * distributor doing its own delivery, whose Fishbowl "carriers" are
	 * internal route/truck codes (PD-14771). Back-office (CRM) and any
	 * real-carrier tenant still show it.
	 */
	showOrderShipVia?: boolean;
	/** Allow authenticated users to view their invoices. Default true. */
	allowInvoiceViewing?: boolean;
	/**
	 * Allow authenticated users to pay invoices through the portal. Default
	 * true. Auto-coerced to false at runtime when no payment gateway is
	 * configured for the portal's company — the UI must render that case
	 * the same as the admin-disabled case.
	 */
	allowInvoicePayments?: boolean;
	/**
	 * Allow authenticated users to view their statements. Default true.
	 * Independent of the invoices toggle so account-focused portals can
	 * shape exactly which financial surfaces appear (PD-14675).
	 */
	allowStatementViewing?: boolean;
	/** Allow authenticated users to view their transactions. Default true. */
	allowTransactionViewing?: boolean;
	/**
	 * Allow authenticated users to reach the customer-support chat (the
	 * floating launcher, the mobile Chat tab, and the chat conversation
	 * surface). Default FALSE — chat is opt-in per portal because rep
	 * routing/staffing has to exist on the tenant side before customers
	 * can be invited to start conversations (PD-14608).
	 */
	allowChat?: boolean;
	/**
	 * Ordered mobile bottom-nav tabs (max 4; 'menu' always enforced).
	 * Absent = the shipped default (home, shop, account, menu). Unknown
	 * keys are dropped and the list is sanitized at resolve time, so a
	 * hand-edited JSON blob can't break the nav (PD-14609).
	 */
	mobileNavTabs?: Array<PortalMobileNavTab>;
	/**
	 * Where authenticated users land after sign-in:
	 *   - `'dashboard'` — the storefront dashboard (default).
	 *   - `'browse'`    — straight into the catalog (requires catalog browsing).
	 *   - `'orders'`    — order history (requires order history).
	 *   - `'account'`   — the account page (always valid).
	 * Capability-validated by the resolver (`coerceAuthenticatedView`): a value
	 * whose prerequisite feature is off can never be served — it collapses to
	 * `'dashboard'`. Default `'dashboard'`.
	 */
	defaultAuthenticatedView?: 'browse' | 'dashboard' | 'orders' | 'account';
	/**
	 * Which sign-in method the storefront's login modal presents FIRST:
	 *   - `'email'` — the email + password form, with "Sign in with phone"
	 *     one link away (the long-standing behavior; default).
	 *   - `'phone'` — the SMS phone-number form leads, with email/password
	 *     one link away. For storefronts whose customers primarily sign in
	 *     by phone (many have no email on file).
	 * Only honored when the tenant actually has phone login enabled
	 * (instance `phoneLoginEnabledPublic`) — the public bootstrap collapses
	 * it back to `'email'` otherwise, so a stale setting can never strand
	 * visitors on a method that doesn't work. Default `'email'`.
	 */
	defaultLoginMethod?: 'email' | 'phone';

	// --- Legacy keys (back-compat for rows written before the avail/qty split) ---
	/** @deprecated use anonymousShowStockAvailability + anonymousShowStockQuantity */
	anonymousShowStock?: boolean;
	/** @deprecated use showStockAvailabilityToAuthenticated + showStockQuantityToAuthenticated */
	showStockToAuthenticated?: boolean;

	// --- Branding ---
	/**
	 * Per-storefront override for the "Powered by X" attribution footer
	 * (PD-14113). Resolved into the public bootstrap with precedence over the
	 * tenant `branding.attribution` instance setting and the brand default;
	 * omit to inherit the tenant/brand value.
	 */
	brandAttribution?: BrandAttributionValue | null;
	/**
	 * Theme for the PRE-LOGIN surfaces only — the welcome/lockout screen
	 * (HomePage), the LoginModal, and (by asset choice) the native splash.
	 * `'dark'` (default) keeps the long-standing dark login identity: dark
	 * surface, white text, the dark-context `logoUrl`. `'light'` renders
	 * those surfaces light: light surface, dark text, the `logoUrlLight`
	 * variant. This is independent of the POST-login storefront theme, which
	 * the user toggles themselves from the profile menu (UserMenu). Default
	 * `'dark'`. Each brand sets its own preference. */
	loginTheme?: 'light' | 'dark';

	// --- Customer support contact (PD-14363) ---
	/**
	 * Customer-service phone number shown on the storefront's pre-login
	 * surfaces (welcome screen, login modal), the desktop header, and the
	 * mobile menu drawer. Display string (e.g. "1-833-272-7378") — rendered
	 * as a `tel:` link with non-dial characters stripped from the href.
	 */
	supportPhone?: string | null;
	/**
	 * Customer-service email shown alongside the phone on the login
	 * surfaces and mobile menu drawer. Rendered as a `mailto:` link.
	 */
	supportEmail?: string | null;
	/**
	 * Free-text business hours shown with the support contact info
	 * (e.g. "Mon-Fri 8:00 AM-5:00 PM CT").
	 */
	supportBusinessHours?: string | null;

	// --- Catalog faceted-filter exposure ---
	/**
	 * Which faceted-search filters this storefront exposes, and in what order.
	 * Each entry references an ItemFilterField by its stable `key` (a built-in
	 * facet like `manufacturer` / `price` / `isInStock`, or a custom field like
	 * `cf_allergen`). The ARRAY ORDER is the display order in the filter rail;
	 * presence = exposed, absence = hidden. `labelOverride` renames the facet for
	 * this site only (falling back to the registry / custom-field default label).
	 *
	 * Per-site only: when this is null/absent the storefront falls back to a
	 * hardcoded system default set. The catalog-config resolver further strips any
	 * field the current visitor can't see (ItemFilterField.gatedBy — e.g. price /
	 * in-stock when pricing / stock is hidden), so admin exposure is necessary but
	 * not sufficient.
	 */
	catalogFilters?: Array<PortalSiteCatalogFilterSetting>;
}

/**
 * One exposed faceted-search filter on a storefront. See
 * PortalSiteSettings.catalogFilters.
 */
export interface PortalSiteCatalogFilterSetting {
	/** ItemFilterField.key — 'manufacturer', 'price', 'isInStock', 'cf_allergen', … */
	key: string;
	/** Optional per-site display label; falls back to the field's default label. */
	labelOverride?: string;
}

/**
 * A `portal_site` is the storefront identity for a single company in a
 * tenant — 1:1 with `company`. There's no notion of a brand spanning
 * multiple companies; "brand" is purely UI sugar over a company's
 * storefront (alias for the URL, logo, email logo, theming).
 *
 * `isDefault` only means "this brand can be reached via the bare
 * <tenant>.my.workd.com URL too" — every brand still has its own alias
 * and the prefixed URL works in addition. There is no brand fallback
 * routing: an unmatched alias is a 404, full stop.
 */
@Index('UK_portal_site_alias', ['alias'], { unique: true })
@Index('UK_portal_site_company', ['fkCompanyId'], { unique: true })
@Entity('portal_site')
export class PortalSiteEntity extends BaseEntity {
	// Required for every brand. Lowercased [a-z0-9]+ (no dashes — dash is
	// the brand/tenant delimiter in the URL). Unique across the tenant.
	@Column('varchar', { name: 'alias', length: 32 })
	public alias!: string;

	// 1:1 with company. Unique across the tenant — exactly one storefront
	// per company. Default 0 so newly-synced rows aren't immediately
	// constraint-violating; service layer requires a real id on create.
	@Column('int', {
		name: 'fk_company__id',
		unsigned: true,
		default: 0,
	})
	public fkCompanyId!: number;

	@Column('varchar', { name: 'displayName', length: 128 })
	public displayName!: string;

	@Column('tinyint', {
		name: 'isActive',
		width: 1,
		default: () => "'1'",
	})
	public isActive!: boolean;

	// "Bare URL maps to this brand too." At most one row per tenant should
	// have isDefault = 1. The aliased URL still works on default rows; this
	// is purely a "no prefix needed" convenience for single-brand tenants
	// (and the tenant's chosen primary brand in multi-brand setups).
	@Column('tinyint', {
		name: 'isDefault',
		width: 1,
		default: () => "'0'",
	})
	public isDefault!: boolean;

	// Brand wordmark for dark-mode / dark-surface contexts (the storefront
	// header is painted with primaryColor and stays dark in both modes, so
	// this is the "default" logo for that surface). Tenants typically upload
	// a white/light wordmark here. Falls back to itself in light mode if
	// `logoUrlLight` is unset, so existing single-asset tenants keep working.
	@Column('varchar', { name: 'logoUrl', nullable: true, length: 512 })
	public logoUrl!: string | null;

	// Brand wordmark variant for light-mode surfaces — used post-login when
	// the tenant has opted into a light mode that paints surfaces white,
	// where a white-on-dark wordmark would disappear. Optional: when null,
	// consumers fall back to `logoUrl`.
	@Column('varchar', { name: 'logoUrlLight', nullable: true, length: 512 })
	public logoUrlLight!: string | null;

	@Column('varchar', { name: 'emailLogoUrl', nullable: true, length: 512 })
	public emailLogoUrl!: string | null;

	/**
	 * Storefront-scoped ecommerce settings. JSON column so we can evolve
	 * without a migration per knob. Fields are intentionally optional in
	 * the type so older rows (stored as `null`) and partial saves both
	 * work; the resolver applies hardcoded defaults for any missing key.
	 *
	 * The API NEVER trusts a client-provided "show price" flag — it always
	 * reads this column server-side after middleware resolves the active
	 * portal_site from the request hostname.
	 */
	@Column('json', { name: 'settings', nullable: true })
	public settings!: PortalSiteSettings | null;

	// Hero image rendered behind the storefront LoginModal (right-side brand
	// panel of the split-pane sign-in screen, full-bleed background on
	// mobile/Capacitor). CDN URL produced by PublicCdnUploadService.
	@Column('varchar', {
		name: 'loginBackgroundUrl',
		nullable: true,
		length: 512,
	})
	public loginBackgroundUrl!: string | null;

	// Storefront favicon. Swapped into <head> on tenant resolution so the
	// browser tab shows the brand mark instead of the generic Workd icon.
	@Column('varchar', { name: 'faviconUrl', nullable: true, length: 512 })
	public faviconUrl!: string | null;

	// Brand primary color as a CSS-compatible hex string (e.g. '#0F1A2A').
	// Surfaced to the storefront via CSS custom properties so Tailwind
	// utilities like `bg-brand-primary` can resolve at runtime per tenant.
	// This is the dark-mode / default value; `primaryColorLight` overrides
	// it post-login when the user has opted into light mode.
	@Column('varchar', { name: 'primaryColor', nullable: true, length: 16 })
	public primaryColor!: string | null;

	// Light-mode override for `primaryColor`. Optional: when null, consumers
	// fall back to `primaryColor` so existing single-color tenants keep
	// working. Only applies post-login (login/welcome/register stay on the
	// default primaryColor).
	@Column('varchar', {
		name: 'primaryColorLight',
		nullable: true,
		length: 16,
	})
	public primaryColorLight!: string | null;

	// Brand accent color as a CSS-compatible hex string (e.g. '#EF6C2D').
	// Used for vibrant CTAs, eyebrows, and energetic moments — paired with
	// primaryColor through the runtime theme provider. This is the
	// dark-mode / default value.
	@Column('varchar', { name: 'accentColor', nullable: true, length: 16 })
	public accentColor!: string | null;

	// Light-mode override for `accentColor`. Optional: when null, consumers
	// fall back to `accentColor`.
	@Column('varchar', {
		name: 'accentColorLight',
		nullable: true,
		length: 16,
	})
	public accentColorLight!: string | null;

	// Page background color in DARK mode (the body / scroll surface behind
	// cards, modals, and the bottom nav). When null, BrandThemeApplier
	// auto-derives a brand-tinted dark from `primaryColor` over near-black
	// so a tenant who only configures `primaryColor` still gets a coherent,
	// non-generic dark mode for free. Configure explicitly for tenants who
	// want a specific dark surface color (e.g. HF Foods sets a deep green
	// here so dark mode reads as "darker HF" instead of generic gray).
	@Column('varchar', {
		name: 'pageBackgroundColor',
		nullable: true,
		length: 16,
	})
	public pageBackgroundColor!: string | null;

	// Page background color in LIGHT mode. When null, BrandThemeApplier
	// auto-derives a subtle brand-tinted off-white from `primaryColor`
	// (4% mix over white) — the existing behavior — so tenants who
	// haven't configured a light page bg keep their current look.
	@Column('varchar', {
		name: 'pageBackgroundColorLight',
		nullable: true,
		length: 16,
	})
	public pageBackgroundColorLight!: string | null;

	// Surface (card / modal / drawer) background color in DARK mode. When
	// null, BrandThemeApplier auto-derives a slightly-lighter-than-page
	// brand-tinted dark from `primaryColor` over near-black so cards
	// always sit visually above the page surface. Configure explicitly
	// when a tenant has a designed elevated-surface color.
	@Column('varchar', {
		name: 'surfaceBackgroundColor',
		nullable: true,
		length: 16,
	})
	public surfaceBackgroundColor!: string | null;

	// Surface (card / modal / drawer) background color in LIGHT mode.
	// When null, defaults to plain white — the long-standing storefront
	// card style. Configure explicitly when a tenant wants tinted cards
	// in light mode (e.g. a soft cream that picks up the page bg tint).
	@Column('varchar', {
		name: 'surfaceBackgroundColorLight',
		nullable: true,
		length: 16,
	})
	public surfaceBackgroundColorLight!: string | null;

	// Solid color shown around/behind the loginBackgroundUrl image whenever
	// the image's aspect ratio doesn't fill the viewport (tall portrait
	// screens, ultra-wide monitors). Should match the dominant edge color
	// of `loginBackgroundUrl` so the transition reads as one continuous
	// scene instead of an image floating on a generic background.
	// Fallback when unset: primaryColor.
	@Column('varchar', {
		name: 'loginBackgroundColor',
		nullable: true,
		length: 16,
	})
	public loginBackgroundColor!: string | null;

	// Master switch for self-serve customer registration on this portal.
	// Off by default — leaving a public write endpoint open on a tenant
	// who hasn't asked for it is a footgun. When false, the public
	// /portal/register endpoint refuses submissions for this brand AND the
	// "Register" link in the storefront login UI is hidden.
	@Column('tinyint', {
		name: 'isRegistrationEnabled',
		width: 1,
		default: () => "'0'",
	})
	public isRegistrationEnabled!: boolean;

	// Per-portal override for the email recipient who gets notified when a
	// new lead lands via /portal/register. Falls back to the tenant-level
	// `notifications.lead_registration_email` instance setting when null,
	// then to a system default if that is also unset.
	@Column('varchar', {
		name: 'leadNotificationEmail',
		nullable: true,
		length: 255,
	})
	public leadNotificationEmail!: string | null;

	// JSON array of fully-qualified origin strings (scheme + host + optional
	// port — e.g. ["https://hffoodsgroup.com"]) that may POST to the public
	// /portal/register endpoint on behalf of this brand from outside our
	// own subdomain. Tenants register their own marketing site origin here
	// so they can host a branded form that submits cross-origin. Our own
	// portal subdomain is auto-trusted server-side and need not be listed.
	// Null/empty means "only our own subdomain may submit."
	@Column('json', { name: 'registrationAllowedOrigins', nullable: true })
	public registrationAllowedOrigins!: Array<string> | null;

	@ManyToOne(() => CompanyEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_company__id', referencedColumnName: 'id' }])
	public fkCompany!: CompanyEntity;

	@OneToMany(
		() => PortalSiteDomainEntity,
		(portalSiteDomain) => portalSiteDomain.fkPortalSite,
	)
	public portalSiteDomains!: Array<PortalSiteDomainEntity>;
}
